import { createFileRoute } from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type FormEvent } from 'react'
import { api } from '../lib/api.js'
import { PageHeader } from '../components/PageHeader.js'
import { Section } from '../components/Section.js'
import { Card, CardBody, CardFooter } from '../components/Card.js'
import { Button } from '../components/Button.js'
import { Field, Input, Select, Textarea } from '../components/Field.js'
import { DataTable, type Column } from '../components/DataTable.js'
import { Empty } from '../components/Empty.js'
import { Icon } from '../components/Icon.js'
import { FeatureGate } from '../components/FeatureGate.js'
import { FEATURES } from '@academy/config'

export const Route = createFileRoute('/_app/exams')({
  component: () => (
    <FeatureGate feature={FEATURES.EXAM_BOARDS}>
      <AdminExamsPage />
    </FeatureGate>
  ),
})

type Board = 'icse' | 'ssc' | 'cbse' | 'state_board' | 'other'

interface ExamRow {
  id: string
  board: Board
  subject: string
  examDate: string
  academicYear: string
  notes: string | null
  createdAt: string
}

const BOARDS: { value: Board; label: string }[] = [
  { value: 'icse', label: 'ICSE' },
  { value: 'ssc', label: 'SSC' },
  { value: 'cbse', label: 'CBSE' },
  { value: 'state_board', label: 'State Board' },
  { value: 'other', label: 'Other' },
]

function AdminExamsPage() {
  const qc = useQueryClient()
  const [error, setError] = useState<string | null>(null)
  const [broadcasting, setBroadcasting] = useState<string | null>(null)
  const [broadcastResult, setBroadcastResult] = useState<string | null>(null)

  const exams = useQuery({
    queryKey: ['exam-boards'],
    queryFn: async () => {
      const res = await api<ExamRow[]>('/api/v1/exam-boards')
      if ('error' in res) throw new Error(res.error.message)
      return res.data
    },
  })

  // Form state
  const [board, setBoard] = useState<Board>('cbse')
  const [subject, setSubject] = useState('')
  const [examDate, setExamDate] = useState('')
  const [academicYear, setAcademicYear] = useState('2026-27')
  const [notes, setNotes] = useState('')

  const create = useMutation({
    mutationFn: async () => {
      const res = await api<ExamRow>('/api/v1/exam-boards', {
        method: 'POST',
        body: JSON.stringify({
          board,
          subject,
          examDate,
          academicYear,
          notes: notes || undefined,
        }),
      })
      if ('error' in res) throw new Error(res.error.message)
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['exam-boards'] })
      setSubject('')
      setExamDate('')
      setNotes('')
      setError(null)
    },
    onError: (err) => setError(err instanceof Error ? err.message : 'Failed'),
  })

  const remove = useMutation({
    mutationFn: async (id: string) => {
      const res = await api(`/api/v1/exam-boards/${id}`, { method: 'DELETE' })
      if ('error' in res) throw new Error(res.error.message)
      return res.data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['exam-boards'] }),
  })

  const broadcast = useMutation({
    mutationFn: async (id: string) => {
      const res = await api<{ sent: number; skipped: number; total: number }>(
        `/api/v1/exam-boards/${id}/broadcast`,
        { method: 'POST', body: JSON.stringify({}) },
      )
      if ('error' in res) throw new Error(res.error.message)
      return res.data
    },
    onSuccess: (data) => {
      setBroadcastResult(
        `Sent to ${data.sent} parent${data.sent === 1 ? '' : 's'}${
          data.skipped > 0 ? ` · ${data.skipped} skipped` : ''
        }`,
      )
      setBroadcasting(null)
    },
    onError: (err) => {
      setError(err instanceof Error ? err.message : 'Broadcast failed')
      setBroadcasting(null)
    },
  })

  const onSubmit = (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    if (!subject || !examDate) {
      setError('Subject and exam date are required')
      return
    }
    create.mutate()
  }

  const columns: Column<ExamRow>[] = [
    {
      key: 'date',
      header: 'Date',
      render: (e) => (
        <div>
          <p className="text-fg font-medium">
            {new Date(e.examDate).toLocaleDateString('en-IN', {
              day: '2-digit',
              month: 'short',
              year: 'numeric',
            })}
          </p>
          <p className="text-xs text-fg-subtle">{e.academicYear}</p>
        </div>
      ),
    },
    {
      key: 'subject',
      header: 'Subject',
      render: (e) => <span className="font-medium text-fg">{e.subject}</span>,
    },
    {
      key: 'board',
      header: 'Board',
      render: (e) => (
        <span className="text-xs uppercase font-medium text-fg-muted">
          {e.board.replace('_', ' ')}
        </span>
      ),
    },
    {
      key: 'notes',
      header: 'Notes',
      render: (e) =>
        e.notes ? (
          <span className="text-sm text-fg-muted line-clamp-1" title={e.notes}>
            {e.notes}
          </span>
        ) : (
          <span className="text-fg-subtle">—</span>
        ),
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      render: (e) => {
        const isBroadcasting = broadcasting === e.id
        return (
          <div className="flex justify-end gap-2 text-xs">
            <button
              type="button"
              onClick={() => {
                setBroadcasting(e.id)
                setBroadcastResult(null)
                broadcast.mutate(e.id)
              }}
              disabled={isBroadcasting}
              className="text-fg-muted hover:text-[var(--color-primary)] disabled:opacity-50"
            >
              {isBroadcasting ? 'Sending…' : 'Broadcast'}
            </button>
            <button
              type="button"
              onClick={() => {
                if (
                  confirm(
                    `Remove ${e.subject} (${e.board.toUpperCase()}) on ${e.examDate}?`,
                  )
                ) {
                  remove.mutate(e.id)
                }
              }}
              className="text-fg-muted hover:text-danger"
            >
              Delete
            </button>
          </div>
        )
      },
    },
  ]

  return (
    <div className="space-y-6">
      <PageHeader
        title="Exam schedules"
        description="Configure upcoming board exams. Broadcast a notice to every parent whose child sits for that board."
      />

      <Section title="Add an exam">
        <Card>
          <form onSubmit={onSubmit}>
            <CardBody className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Field label="Board" required>
                <Select value={board} onChange={(e) => setBoard(e.target.value as Board)}>
                  {BOARDS.map((b) => (
                    <option key={b.value} value={b.value}>
                      {b.label}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label="Subject" required>
                <Input
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="Mathematics"
                  required
                />
              </Field>
              <Field label="Exam date" required>
                <Input
                  type="date"
                  value={examDate}
                  onChange={(e) => setExamDate(e.target.value)}
                  required
                />
              </Field>
              <Field label="Academic year" required>
                <Input
                  value={academicYear}
                  onChange={(e) => setAcademicYear(e.target.value)}
                  placeholder="2026-27"
                  required
                />
              </Field>
              <Field
                label="Notes / syllabus pointers"
                optional
                className="md:col-span-4"
              >
                <Textarea
                  rows={2}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Algebra, mensuration, statistics. Calculator allowed."
                />
              </Field>
            </CardBody>
            <CardFooter className="flex items-center justify-between">
              {error ? (
                <p className="inline-flex items-center gap-1 text-sm text-danger">
                  <Icon name="alert" size={14} />
                  {error}
                </p>
              ) : broadcastResult ? (
                <p className="inline-flex items-center gap-1 text-sm text-success">
                  <Icon name="check" size={14} />
                  {broadcastResult}
                </p>
              ) : (
                <span />
              )}
              <Button
                type="submit"
                loading={create.isPending}
                disabled={!subject || !examDate}
              >
                Add exam
              </Button>
            </CardFooter>
          </form>
        </Card>
      </Section>

      <Section
        title="Upcoming exams"
        description={`${exams.data?.length ?? 0} scheduled`}
      >
        {exams.data && exams.data.length === 0 ? (
          <Card>
            <Empty
              icon="clipboardCheck"
              title="No exam schedules yet"
              description="Add your first exam above. Broadcasting will notify only parents whose children sit that board."
            />
          </Card>
        ) : (
          <DataTable
            columns={columns}
            rows={exams.data ?? []}
            rowKey={(e) => e.id}
            loading={exams.isLoading}
            density="compact"
            stickyHeader
            empty="No exams match these filters."
          />
        )}
      </Section>
    </div>
  )
}
